import { useState, useEffect } from "react";
import {
  useCreateShape,
  useOneShape,
  useUpdateShape,
} from "http-hooks/shape/useShape";
import { Switch } from "@headlessui/react";
import Input from "components/ui/Input";
import Modal from "components/ui/modals";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SetShape({ id, onClose, open }) {
  const [shapeDetails, setShapeDetails] = useState({
    shapetype: "",
    ag_status: 1,
  });

  const { isLoading: isLoadingShape, data } = useOneShape(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateShape();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateShape();

  const isPending = isCreating || isUpdating;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setShapeDetails((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleStatusChange = (e) => {
    setShapeDetails((prevData) => ({ ...prevData, ag_status: e ? 1 : 0 }));
  };

  useEffect(() => {
    if (data) {
      setShapeDetails((prev) => {
        const updatedDetails = {};
        Object.keys(prev).forEach((key) => {
          updatedDetails[key] = data[key] ?? prev[key];
        });
        return updatedDetails;
      });
    }
  }, [data]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await mutateUpdate({ id, data: shapeDetails });
      } else {
        await mutateCreate(shapeDetails);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Shape
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-shape-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoadingShape ? (
        <div className="animate-pulse space-y-6">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-6 bg-gray-200 rounded w-12" />
        </div>
      ) : (
        <form id="set-shape-form" onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="text"
            name="shapetype"
            id="shapetype"
            label="Shape Type"
            maxLength="10"
            value={shapeDetails.shapetype}
            onChange={handleInputChange}
          />
          {id && (
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Status
              </label>
              <Switch
                checked={!!shapeDetails.ag_status}
                onChange={handleStatusChange}
                className={classNames(
                  shapeDetails.ag_status ? "bg-indigo-600" : "bg-gray-200",
                  "relative inline-flex ml-1 mt-3 h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    shapeDetails.ag_status ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
